import React, { useEffect, useState } from 'react';

import { Input as AInput, Form, Modal } from 'antd';
import TrackingButton from 'components/TrackingComponents/Button';
import TrackingInput from 'components/TrackingComponents/TrackingInput';
import { popupSigninSubmit, setOpenSigninPopup } from 'containers/MainLayout/actions';
import { selectOpenSigninPopup } from 'containers/MainLayout/selectors';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { ApplicationRootState } from 'types/index';
import utilsMessages from 'utils/messages';
import Checkbox from '../Checkbox/Checkbox';
import SVGIcon from '../SVGIcon/SVGIcon';
import CloseIcon from './CloseIcon.svg';
import messages from './messages';

const AntdInput = styled(AInput)<any>`
  height: 44px;
`;
const AntdPassword = styled(AInput.Password)<any>`
  height: 44px;
`;

const CustomModal = styled(Modal)`
  .ant-modal-content {
    padding: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  width: 100% !important;
  max-width: 480px !important;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 16px;

  overflow: hidden;

  .ant-form-item-label > label {
    width: 100%;
  }

  .ant-form-item .ant-form-item-label > label.ant-form-item-required::before {
    content: '';
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;

  .ant-form-item-label > label {
    width: 100%;
  }

  .ant-form-item .ant-form-item-label > label.ant-form-item-required::before {
    display: none;
  }
  .ant-form-item .ant-form-item-label > label.ant-form-item-required::after {
    display: none;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseIconWrapper = styled.div`
  padding: 4px;
  cursor: pointer;
  width: 32px;
  height: 32px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  line-height: 32px;
  color: #111827;
  margin-bottom: 4px;
  text-align: center;
`;

const SubTitle = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: #4b5563;
  font-weight: 400;
  text-align: center;
  margin-bottom: 16px;
`;

const PasswordLabelContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LabelText = styled.span`
  font-size: 14px;
  line-height: 22px;
  color: #4b5563;
  font-weight: 500;
  text-transform: none;

  :after {
    content: '*';
    font-size: 14px;
    color: #e2483c;
    font-weight: 600;
    margin-inline-start: 2px;
    line-height: 20px;
    visibility: visible;
  }
`;

const ForgotPasswordText = styled.span`
  font-size: 14px;
  line-height: 22px;
  color: #3c9041;
  font-weight: 500;

  :hover {
    color: #3c9041;
    cursor: pointer;
  }
`;

const ActionsButton = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SignInButton = styled(Form.Item)`
  &.ant-form-item {
    margin-bottom: 0px !important;
  }
  width: 100% !important;
`;

const GoToSignupSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const NotHaveAccountYet = styled.span`
  font-size: 14px;
  line-height: 22px;
  color: #111827;
  font-weight: 400;
`;

const SignUpLink = styled(Link)`
  font-size: 14px;
  line-height: 22px;
  color: #3c9041 !important;
  font-weight: 500 !important;

  :hover {
    color: #3c9041 !important;
    cursor: pointer;
  }
`;

interface DispatchProps {
  onSubmit: (values: any) => void;
  onOpenSigninPopup: (value: boolean) => void;
}

interface IStateProps {
  openSigninPopup: boolean;
}

type Props = DispatchProps & IStateProps;

const SigninPopup = ({ onSubmit, openSigninPopup, onOpenSigninPopup }: Props) => {
  const [form] = Form.useForm();

  const previousLoginEmail = window.localStorage.getItem('previousLoginEmail');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      email: previousLoginEmail || '',
    });
  }, [previousLoginEmail]);

  useEffect(() => {
    if (!openSigninPopup) {
      form.resetFields(['password']);
    }
  }, [openSigninPopup]);

  return (
    <CustomModal open={openSigninPopup} closable={false} footer={false} centered>
      <Container>
        <ContentWrapper>
          <HeaderWrapper>
            <CloseIconWrapper />
            <Title>
              <FormattedMessage {...messages.header} />
            </Title>
            <CloseIconWrapper
              onClick={() => {
                onOpenSigninPopup(false);
              }}
            >
              <SVGIcon src={CloseIcon} renderAsImg={false} />
            </CloseIconWrapper>
          </HeaderWrapper>
          <SubTitle>
            <FormattedMessage {...messages.subHeader} />
          </SubTitle>
          <Form
            onFinish={(values) => {
              setIsLoading(true);
              onSubmit({
                ...values,
                callback: (success) => {
                  setIsLoading(false);
                },
              });
            }}
            form={form}
            className="login-form"
            size="large"
            layout="vertical"
          >
            <FormattedMessage {...utilsMessages.email}>
              {(email) => (
                <FormattedMessage {...utilsMessages.fieldEmail}>
                  {(fieldEmail) => (
                    <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: 'Email' }}>
                      {(fieldRequired) => (
                        <Form.Item
                          name="email"
                          colon={true}
                          label={<LabelText>Email</LabelText>}
                          hasFeedback
                          rules={[
                            { type: 'email', message: fieldEmail },
                            { required: true, message: fieldRequired },
                          ]}
                          normalize={(value) => value && value.replace(/\s/g, '')}
                        >
                          <TrackingInput
                            placeholder="Email"
                            autoComplete="username"
                            name="email"
                            options={{
                              action: 'Enter Email',
                              category: 'Signin Form | Input',
                            }}
                          >
                            <AntdInput />
                          </TrackingInput>
                        </Form.Item>
                      )}
                    </FormattedMessage>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
            <FormattedMessage {...utilsMessages.password}>
              {(password) => (
                <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: password }}>
                  {(fieldRequired) => (
                    <Form.Item
                      name="password"
                      colon={true}
                      rules={[
                        {
                          required: true,
                          message: fieldRequired,
                        },
                      ]}
                      style={{
                        marginBottom: '8px',
                      }}
                      label={
                        <PasswordLabelContainer>
                          <LabelText>{password}</LabelText>
                        </PasswordLabelContainer>
                      }
                    >
                      <TrackingInput
                        name="password"
                        autoComplete="current-password"
                        placeholder={password}
                        options={{
                          action: 'Enter Password',
                          category: 'Signin Form | Input',
                        }}
                      >
                        <AntdPassword />
                      </TrackingInput>
                    </Form.Item>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>

            <Form.Item
              name="rememberMe"
              colon={false}
              required={false}
              valuePropName="checked"
              initialValue={localStorage.getItem('rememberMe') === 'true'}
              noStyle={true}
            >
              <HeaderWrapper>
                <Checkbox>
                  <FormattedMessage {...messages.rememberMe} />
                </Checkbox>

                <Link to="/forgot-password" onClick={() => onOpenSigninPopup(false)}>
                  <ForgotPasswordText>
                    <FormattedMessage {...messages.forgotPassword} />
                  </ForgotPasswordText>
                </Link>
              </HeaderWrapper>
            </Form.Item>
            <ActionsButton>
              <SignInButton>
                <TrackingButton
                  block
                  type="bg-primary"
                  htmlType="submit"
                  trackingCategory="Signin Form | Button"
                  trackingAction="Submit Signin Form"
                  loading={isLoading}
                >
                  <FormattedMessage {...messages.header} />
                </TrackingButton>
              </SignInButton>
              <GoToSignupSection>
                <NotHaveAccountYet>
                  <FormattedMessage {...messages.notHasAccountYet} />
                </NotHaveAccountYet>
                <SignUpLink to="/contact-us" onClick={() => onOpenSigninPopup(false)}>
                  <FormattedMessage {...messages.signup} />
                </SignUpLink>
              </GoToSignupSection>
            </ActionsButton>
          </Form>
        </ContentWrapper>
      </Container>
    </CustomModal>
  );
};

const mapStateToProps = createStructuredSelector<ApplicationRootState, IStateProps>({
  openSigninPopup: selectOpenSigninPopup(),
});

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    onSubmit: (values) => dispatch(popupSigninSubmit(values)),
    onOpenSigninPopup: (value) => dispatch(setOpenSigninPopup(value)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default withConnect(SigninPopup);
